/**
 * Small design-system primitives. The look is deliberately quiet (neutral zinc, one ink-coloured
 * primary action, hairline borders) so CRA labels and data carry the colour on dense desk pages.
 */
import clsx from "clsx";
import {
  forwardRef,
  useEffect,
  type AnchorHTMLAttributes,
  useId,
  useRef,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from "react";

type Variant = "primary" | "secondary" | "ghost" | "danger";
type Size = "sm" | "md";

const VARIANTS: Record<Variant, string> = {
  primary:
    "bg-zinc-900 text-white hover:bg-zinc-700 dark:bg-zinc-100 dark:text-zinc-900 dark:hover:bg-zinc-300",
  secondary:
    "border border-zinc-300 bg-white text-zinc-800 hover:bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100 dark:hover:bg-zinc-800",
  ghost: "text-zinc-700 hover:bg-zinc-100 dark:text-zinc-300 dark:hover:bg-zinc-800",
  danger:
    "border border-rose-300 bg-white text-rose-700 hover:bg-rose-50 dark:border-rose-800 dark:bg-zinc-900 dark:text-rose-300 dark:hover:bg-rose-950",
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  busy?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "secondary", size = "md", busy, className, children, disabled, type, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type ?? "button"}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      className={clsx(
        "inline-flex items-center justify-center gap-1.5 rounded-md font-medium whitespace-nowrap transition-colors",
        "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-600",
        "disabled:cursor-not-allowed disabled:opacity-50",
        size === "sm" ? "h-7 px-2.5 text-xs" : "h-8 px-3 text-sm",
        VARIANTS[variant],
        className,
      )}
      {...rest}
    >
      {busy ? <Spinner className="h-3.5 w-3.5" /> : null}
      {children}
    </button>
  );
});

/** Anchor styled like a button (downloads, external links). */
export function LinkButton({
  className,
  variant = "secondary",
  size = "md",
  ...rest
}: AnchorHTMLAttributes<HTMLAnchorElement> & { variant?: Variant; size?: Size }) {
  return (
    <a
      className={clsx(
        "inline-flex items-center justify-center gap-1.5 rounded-md font-medium whitespace-nowrap",
        "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-600",
        size === "sm" ? "h-7 px-2.5 text-xs" : "h-8 px-3 text-sm",
        VARIANTS[variant],
        className,
      )}
      {...rest}
    />
  );
}

const CONTROL =
  "block rounded-md border border-zinc-300 bg-white px-2.5 text-sm text-zinc-900 shadow-xs " +
  "placeholder:text-zinc-400 focus:border-sky-600 focus:outline-none focus:ring-1 focus:ring-sky-600 " +
  "disabled:bg-zinc-50 disabled:text-zinc-500 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100 " +
  "dark:placeholder:text-zinc-500 dark:disabled:bg-zinc-800";

/**
 * Controls fill their container unless the caller sets a width. Both `w-full` and `w-56` in one
 * class list left the winner to stylesheet order, and `w-full` won: filters meant to be narrow
 * stretched across the page.
 */
function widthOr(className: string | undefined): string | undefined {
  return /(^|\s)(w-|flex-1)/.test(className ?? "") ? undefined : "w-full";
}

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, ...rest }, ref) {
    return <input ref={ref} className={clsx(CONTROL, widthOr(className), "h-8", className)} {...rest} />;
  },
);

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(
  function Select({ className, ...rest }, ref) {
    return <select ref={ref} className={clsx(CONTROL, widthOr(className), "h-8 pr-7", className)} {...rest} />;
  },
);

export const Textarea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement>
>(function Textarea({ className, ...rest }, ref) {
  return <textarea ref={ref} className={clsx(CONTROL, widthOr(className), "py-2 font-mono", className)} {...rest} />;
});

/** Label + control + optional hint. The control is passed as a render function to wire ids. */
export function Field({
  label,
  hint,
  className,
  children,
}: {
  label: ReactNode;
  hint?: ReactNode;
  className?: string;
  children: (id: string) => ReactNode;
}) {
  const id = useId();
  return (
    <div className={clsx("min-w-0", className)}>
      <label htmlFor={id} className="mb-1 block text-xs font-medium text-zinc-600 dark:text-zinc-400">
        {label}
      </label>
      {children(id)}
      {hint ? <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">{hint}</p> : null}
    </div>
  );
}

export function Checkbox({
  label,
  className,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { label: ReactNode }) {
  return (
    <label className={clsx("inline-flex items-center gap-2 text-sm", className)}>
      <input
        type="checkbox"
        className="h-4 w-4 rounded border-zinc-300 text-zinc-900 accent-zinc-900 dark:border-zinc-600 dark:accent-zinc-100"
        {...rest}
      />
      <span>{label}</span>
    </label>
  );
}

/** Accessible switch (role="switch") used for enable/disable and the NPRM what-if. */
export function Switch({
  checked,
  onChange,
  label,
  disabled,
  description,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: ReactNode;
  disabled?: boolean;
  description?: ReactNode;
}) {
  const id = useId();
  return (
    <div className="flex items-start gap-2">
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={clsx(
          "relative mt-0.5 inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors",
          "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-600 disabled:opacity-50",
          checked ? "bg-zinc-900 dark:bg-zinc-100" : "bg-zinc-300 dark:bg-zinc-700",
        )}
      >
        <span
          aria-hidden
          className={clsx(
            "inline-block h-4 w-4 rounded-full bg-white shadow transition-transform dark:bg-zinc-900",
            checked ? "translate-x-4" : "translate-x-0.5",
          )}
        />
      </button>
      <label htmlFor={id} className="text-sm leading-6">
        {label}
        {description ? (
          <span className="block text-xs text-zinc-500 dark:text-zinc-400">{description}</span>
        ) : null}
      </label>
    </div>
  );
}

export function Panel({
  title,
  actions,
  children,
  className,
  bodyClassName,
  subtitle,
}: {
  title?: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
}) {
  const headingId = useId();
  return (
    // Named by its heading, so each panel is a landmark screen readers (and tests) can jump to.
    <section
      aria-labelledby={title ? headingId : undefined}
      className={clsx(
        "rounded-lg border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900/60",
        className,
      )}
    >
      {title || actions ? (
        <header className="flex flex-wrap items-center gap-2 border-b border-zinc-200 px-4 py-2.5 dark:border-zinc-800">
          <div className="min-w-0">
            {title ? (
              <h2 id={headingId} className="text-sm font-semibold">
                {title}
              </h2>
            ) : null}
            {subtitle ? (
              <p className="text-xs text-zinc-500 dark:text-zinc-400">{subtitle}</p>
            ) : null}
          </div>
          {actions ? <div className="ml-auto flex flex-wrap items-center gap-2">{actions}</div> : null}
        </header>
      ) : null}
      <div className={clsx("p-4", bodyClassName)}>{children}</div>
    </section>
  );
}

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="mb-5 flex flex-wrap items-end gap-3">
      <div className="min-w-0">
        <h1 id="page-title" className="text-xl font-semibold tracking-tight">
          {title}
        </h1>
        {subtitle ? (
          <p className="mt-0.5 text-sm text-zinc-500 dark:text-zinc-400">{subtitle}</p>
        ) : null}
      </div>
      {actions ? <div className="ml-auto flex flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  );
}

type Tone = "neutral" | "info" | "good" | "warn" | "bad";

const TONES: Record<Tone, string> = {
  neutral: "bg-zinc-100 text-zinc-700 ring-zinc-500/20 dark:bg-zinc-800 dark:text-zinc-300",
  info: "bg-sky-50 text-sky-800 ring-sky-600/20 dark:bg-sky-950 dark:text-sky-300",
  good: "bg-emerald-50 text-emerald-800 ring-emerald-600/20 dark:bg-emerald-950 dark:text-emerald-300",
  warn: "bg-amber-50 text-amber-900 ring-amber-600/25 dark:bg-amber-950 dark:text-amber-300",
  bad: "bg-rose-50 text-rose-800 ring-rose-600/20 dark:bg-rose-950 dark:text-rose-300",
};

export function Badge({
  tone = "neutral",
  children,
  className,
  title,
}: {
  tone?: Tone;
  children: ReactNode;
  className?: string;
  title?: string;
}) {
  return (
    <span
      title={title}
      className={clsx(
        "inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium whitespace-nowrap ring-1 ring-inset",
        TONES[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

/** Callout banner. `role="alert"` only for errors so screen readers aren't spammed. */
export function Notice({
  tone = "info",
  title,
  children,
  className,
  actions,
}: {
  tone?: Tone;
  title?: ReactNode;
  children?: ReactNode;
  className?: string;
  actions?: ReactNode;
}) {
  const border: Record<Tone, string> = {
    neutral: "border-zinc-300 bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-900",
    info: "border-sky-300 bg-sky-50 text-sky-950 dark:border-sky-800 dark:bg-sky-950/60 dark:text-sky-100",
    good: "border-emerald-300 bg-emerald-50 text-emerald-950 dark:border-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-100",
    warn: "border-amber-300 bg-amber-50 text-amber-950 dark:border-amber-800 dark:bg-amber-950/60 dark:text-amber-100",
    bad: "border-rose-300 bg-rose-50 text-rose-950 dark:border-rose-800 dark:bg-rose-950/60 dark:text-rose-100",
  };
  return (
    <div
      role={tone === "bad" ? "alert" : "status"}
      className={clsx("flex flex-wrap items-start gap-3 rounded-md border px-3 py-2 text-sm", border[tone], className)}
    >
      <div className="min-w-0 flex-1">
        {title ? <p className="font-medium">{title}</p> : null}
        {children ? <div className={clsx(title ? "mt-0.5" : "", "text-[13px]")}>{children}</div> : null}
      </div>
      {actions ? <div className="flex items-center gap-2">{actions}</div> : null}
    </div>
  );
}

export function Spinner({ className }: { className?: string }) {
  return (
    <svg
      className={clsx("animate-spin text-current", className ?? "h-4 w-4")}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden
    >
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

export interface TabItem<K extends string> {
  key: K;
  label: ReactNode;
  badge?: ReactNode;
}

export function Tabs<K extends string>({
  items,
  value,
  onChange,
  className,
  label,
}: {
  items: TabItem<K>[];
  value: K;
  onChange: (k: K) => void;
  className?: string;
  label: string;
}) {
  return (
    <div
      role="tablist"
      aria-label={label}
      className={clsx("flex flex-wrap gap-1 border-b border-zinc-200 dark:border-zinc-800", className)}
      // WAI-ARIA tabs: one tab stop for the list, arrow keys (and Home/End) move between tabs.
      onKeyDown={(e) => {
        const i = items.findIndex((t) => t.key === value);
        const next =
          e.key === "ArrowRight" ? (i + 1) % items.length
          : e.key === "ArrowLeft" ? (i - 1 + items.length) % items.length
          : e.key === "Home" ? 0
          : e.key === "End" ? items.length - 1
          : -1;
        if (next < 0 || !items[next]) return;
        e.preventDefault();
        onChange(items[next]!.key);
        const buttons = e.currentTarget.querySelectorAll<HTMLButtonElement>('[role="tab"]');
        buttons[next]?.focus();
      }}
    >
      {items.map((t) => (
        <button
          key={t.key}
          type="button"
          role="tab"
          aria-selected={t.key === value}
          tabIndex={t.key === value ? 0 : -1}
          onClick={() => onChange(t.key)}
          className={clsx(
            "-mb-px inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium",
            t.key === value
              ? "border-zinc-900 text-zinc-900 dark:border-zinc-100 dark:text-zinc-100"
              : "border-transparent text-zinc-500 hover:text-zinc-800 dark:text-zinc-400 dark:hover:text-zinc-200",
          )}
        >
          {t.label}
          {t.badge}
        </button>
      ))}
    </div>
  );
}

/** KPI tile: the number is the chart (dataviz "stat tile"); proportional figures. */
export function StatTile({
  label,
  value,
  sub,
  tone,
}: {
  label: ReactNode;
  value: ReactNode;
  sub?: ReactNode;
  tone?: "default" | "muted";
}) {
  return (
    <div className="rounded-lg border border-zinc-200 bg-white px-3 py-2.5 dark:border-zinc-800 dark:bg-zinc-900/60">
      <p className="text-xs font-medium text-zinc-500 dark:text-zinc-400">{label}</p>
      <p
        className={clsx(
          "mt-1 text-xl font-semibold tracking-tight",
          tone === "muted" ? "text-zinc-500" : "text-zinc-900 dark:text-zinc-50",
        )}
      >
        {value}
      </p>
      {sub ? <p className="mt-0.5 text-xs text-zinc-500 dark:text-zinc-400">{sub}</p> : null}
    </div>
  );
}

/** Two-column fact list (label / value) used by profile and metrics panels. */
export function FactList({
  items,
  className,
}: {
  items: { label: ReactNode; value: ReactNode; key?: string }[];
  className?: string;
}) {
  return (
    <dl className={clsx("grid grid-cols-[minmax(8rem,auto)_1fr] gap-x-4 gap-y-1.5 text-sm", className)}>
      {items.map((it, i) => (
        <div key={it.key ?? i} className="contents">
          <dt className="text-zinc-500 dark:text-zinc-400">{it.label}</dt>
          <dd className="min-w-0 break-words tabular-nums">{it.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/** Native <dialog> modal: focus trapping and Esc come for free from the platform. */
export function Dialog({
  open,
  onClose,
  title,
  children,
  footer,
  wide,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // jsdom has no showModal; fall back to the open attribute so tests can render dialogs.
    if (open && !el.open) {
      if (typeof el.showModal === "function") el.showModal();
      else el.setAttribute("open", "");
    } else if (!open && el.open) {
      if (typeof el.close === "function") el.close();
      else el.removeAttribute("open");
    }
  }, [open]);
  return (
    <dialog
      ref={ref}
      // Without a name a screen reader announces only "dialog".
      aria-labelledby={titleId}
      onClose={onClose}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      className={clsx(
        "m-auto w-full rounded-lg border border-zinc-200 bg-white p-0 text-zinc-900 shadow-xl backdrop:bg-zinc-950/40",
        "dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100",
        wide ? "max-w-5xl" : "max-w-lg",
      )}
    >
      {open ? (
        <div className="flex max-h-[85vh] flex-col">
          <header className="flex items-center gap-2 border-b border-zinc-200 px-4 py-3 dark:border-zinc-800">
            <h2 id={titleId} className="text-base font-semibold">
              {title}
            </h2>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close dialog"
              className="ml-auto rounded p-1 text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800"
            >
              ✕
            </button>
          </header>
          <div className="overflow-y-auto px-4 py-3">{children}</div>
          {footer ? (
            <footer className="flex justify-end gap-2 border-t border-zinc-200 px-4 py-3 dark:border-zinc-800">
              {footer}
            </footer>
          ) : null}
        </div>
      ) : null}
    </dialog>
  );
}

export function ProgressBar({ fraction, label }: { fraction: number | null; label: string }) {
  const pct = fraction === null ? null : Math.round(Math.max(0, Math.min(1, fraction)) * 100);
  return (
    <div
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={pct ?? undefined}
      className="h-1.5 w-full overflow-hidden rounded-full bg-zinc-200 dark:bg-zinc-800"
    >
      <div
        className={clsx(
          "h-full rounded-full bg-zinc-800 transition-[width] dark:bg-zinc-200",
          pct === null && "w-1/3 animate-pulse",
        )}
        style={pct === null ? undefined : { width: `${pct}%` }}
      />
    </div>
  );
}

export function ExternalLink({ href, children, className }: { href: string; children: ReactNode; className?: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={clsx("text-sky-700 underline-offset-2 hover:underline dark:text-sky-400", className)}
    >
      {children}
      <span aria-hidden> ↗</span>
    </a>
  );
}
